import { BookOutlined, PlusOutlined } from "@ant-design/icons"
import { Breadcrumb, Button, Layout, Table, Row, Col, Spin } from "antd"
import { AxiosError } from "axios"
import { LayoutPageContent, LayoutPageHeader } from "components"
import usePlatform from "contexts/platform/use-platform"
import { TErrorState } from "models/errors"
import { IPolicyholder, PolicyholderType } from "platform-client/types"
import React, { useState, useEffect } from "react"
import { useQuery } from "react-query"
import { RouteComponentProps, useHistory, withRouter } from "react-router"
import { Link } from "react-router-dom"
import { getUserTokenValue } from "utils/pocketknife/get-user-token-value"
import { useCompaniesMap } from "../../services/hooks/use-companies-map"
import { SearchComponent } from "./components/Search"

function getPolicyholderDisplayName(record: IPolicyholder): string {
  const businessName = record.businessName
  const contactName = record.contact?.name

  if (businessName) {
    const contactNameParts = [
      contactName?.firstName,
      contactName?.lastName
    ].filter(Boolean)
    const contactFullName =
      contactNameParts.length > 0 ? contactNameParts.join(" ") : null
    return contactFullName
      ? `${contactFullName} - ${businessName}`
      : businessName
  }

  if (contactName) {
    const nameParts = [contactName.firstName, contactName.lastName].filter(
      Boolean
    )
    if (nameParts.length > 0) return nameParts.join(" ")
  }

  return record.legalName || record.businessName || "-"
}

const columns = (
  {
    onEditClick
  }: {
    onEditClick: (clientId: string) => void
  },
  userGroup: unknown,
  companiesMap?: Map<string, string>,
  isLoadingCompanies?: boolean
) => [
  {
    title: "Company Name / Office",
    key: "companyName",
    render: (record: IPolicyholder) => {
      if (!record.companyID) return "-"
      if (isLoadingCompanies) {
        return (
          <>
            <Spin size="small" style={{ marginRight: 8 }} />
            <span style={{ color: "#999999" }}>Loading...</span>
          </>
        )
      }
      if (!companiesMap) return "-"
      return companiesMap.get(record.companyID) || "-"
    }
  },
  {
    title: "Client ID",
    dataIndex: "referenceID",
    key: "referenceID",
    render: (referenceID: string | null) => referenceID || "-"
  },
  {
    title: "Name",
    key: "name",
    render: (record: IPolicyholder) => {
      const displayName = getPolicyholderDisplayName(record)
      if (displayName === "-") return "-"
      return (
        <Link to={`/client/${record.id}/summary`}>
          <u>{displayName}</u>
        </Link>
      )
    }
  },
  {
    title: "Client Type",
    dataIndex: "policyholderType",
    key: "policyholderType",
    render: (policyholderType: PolicyholderType) => policyholderType || "-"
  },
  {
    title: "Contact Email Address",
    key: "contact.emailAddress",
    render: (record: IPolicyholder) => {
      const emailAddress = record.contact?.emailAddress
      return emailAddress || "-"
    }
  },
  {
    title: "Actions",
    key: "actions",
    render: ({ id }: { id: string }) => {
      return <Button onClick={() => onEditClick(id)}>Edit</Button>
    }
  }
]

const ListClients: React.FC<ListClientsProps> = (props) => {
  const [apiError, setApiError] = React.useState<TErrorState>()
  const [searchValue, setSearchValue] = useState<string | undefined | null>(
    undefined
  )
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [policyholdersData, setPolicyholdersData] = useState<
    IPolicyholder[] | undefined
  >()
  const history = useHistory()
  const platform = usePlatform()
  const userGroupType = getUserTokenValue("grouptype", platform.token || "")

  const listPolicyholders = useQuery(
    ["listPolicyholders", "Policyholder"],
    () => platform.policyholders.listPolicyholders(),
    {
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.code ? parseInt(error.code) : 500,
          validationMessage: "Unable to load clients"
        })
      },
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true
    }
  )

  const displayedPolicyholders = React.useMemo(() => {
    return policyholdersData ?? listPolicyholders.data
  }, [policyholdersData, listPolicyholders.data])

  const { companiesMap, isLoading: isLoadingCompanies } = useCompaniesMap(
    displayedPolicyholders
  )

  useEffect(() => {
    if (!searchValue) {
      setPolicyholdersData(undefined)
    }
  }, [searchValue])

  return (
    <Layout>
      <LayoutPageHeader
        ghost={false}
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item key="/clients">
              <BookOutlined />
              &nbsp;Clients
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        title={"All Clients"}
        extra={
          <Link to={"/clients/create"} className="ant-btn ant-btn-primary">
            <PlusOutlined /> Create client
          </Link>
        }
      />
      <LayoutPageContent error={apiError}>
        <Row
          justify="end"
          align="middle"
          style={{
            marginBottom: 16,
            paddingTop: 16,
            textAlign: "right",
            paddingRight: 16
          }}
        >
          <Col span={9}>
            <SearchComponent
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setSearchLoading={setSearchLoading}
              policyholdersUpdate={setPolicyholdersData}
            />
          </Col>
        </Row>
        <Table
          rowKey="id"
          scroll={{ x: "max-content" }}
          loading={listPolicyholders.isLoading || isSearchLoading}
          columns={columns(
            {
              onEditClick: (clientId: string) =>
                history.push(`/clients/edit/${clientId}`)
            },
            userGroupType,
            companiesMap,
            isLoadingCompanies
          )}
          dataSource={displayedPolicyholders}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ["10", "20"],
            showSizeChanger: true,
            hideOnSinglePage: false,
            position: ["bottomCenter"],
            size: "small"
          }}
        />
      </LayoutPageContent>
    </Layout>
  )
}

export type ListClientsProps = RouteComponentProps<{
  type: string
}>

export default withRouter(ListClients)
